import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormikContextType } from 'formik';
import { Box } from '@mui/material';
import { UIContext } from '../context/UIContext';
import FormikAddressGoogleAutocomplete from './formikComponents/FormikAddressGoogleAutocomplete';
import FormikTextField from './formikComponents/FormikTextField';
import FormikSelect from './formikComponents/FormikSelect';
import {
  getAddressComponentOfTypeLongName,
  getAddressComponentOfTypeShortName,
  salesforceCountyOptionsByCountry,
} from '../helpers/helpers';
import useCountries from '../helpers/useCountries';
import { AddressComponents, PlacesAutocompletePrediction } from '../types';

const AddressFormWithGoogleAutocomplete = ({
  formik,
  formName,
  translationKey,
  extraAddressLine = undefined,
  allFieldsFullWidth = false,
}: {
  formik: FormikContextType<any>;
  formName: string;
  translationKey: string;
  extraAddressLine?: JSX.Element;
  allFieldsFullWidth?: boolean;
}) => {
  const { t } = useTranslation();
  const { isMobile } = useContext(UIContext);
  const { countries } = useCountries();
  const [countyList, setCountyList] = useState([]);

  useEffect(() => {
    if (countries && countries?.length > 0) {
      const countryCode = countries?.find(
        (country) =>
          country?.label === formik?.values?.country || country?.value === formik?.values?.country
      )?.value;
      const newCountyList = salesforceCountyOptionsByCountry(countryCode);

      // Set options list for that country
      setCountyList(newCountyList);

      const isCurrentCountyInList = newCountyList?.find(
        (county) =>
          county?.label === formik?.values?.county || county?.value === formik?.values?.county
      );
      if (formik?.values?.county !== '' && !isCurrentCountyInList) {
        // Reset county field in case it's a country without counties, so no value goes on the field
        // The check above is so we don't delete the first pre-filled value
        formik.setFieldValue('county', '');
      }
    }
  }, [countries, formik?.values?.country]);

  const setFormikAddressValues = useCallback(
    (addressContents: AddressComponents[], prediction: PlacesAutocompletePrediction) => {
      formik.setFieldValue('addressLine1', prediction.Description);
      formik.setFieldValue(
        'town',
        getAddressComponentOfTypeLongName(addressContents, 'postal_town') ||
          getAddressComponentOfTypeLongName(addressContents, 'locality') ||
          ''
      );
      formik.setFieldValue(
        'postCode',
        getAddressComponentOfTypeLongName(addressContents, 'postal_code') || ''
      );

      const newCountry = getAddressComponentOfTypeShortName(addressContents, 'country') || '';
      formik.setFieldValue('country', newCountry);

      const newCounty =
        getAddressComponentOfTypeShortName(addressContents, 'administrative_area_level_2') || '';

      // Check if county is in SF list for the picked country and if yes, set it
      const countryCode = countries?.find(
        (country) => country?.label === newCountry || country?.value === newCountry
      )?.value;
      const newCountyList = salesforceCountyOptionsByCountry(countryCode);
      const isCurrentCountyInList = newCountyList?.find(
        (county) => county?.label === newCounty || county?.value === newCounty
      );

      if (isCurrentCountyInList) {
        formik.setFieldValue('county', newCounty);
      } else {
        formik.setFieldValue('county', '');
      }
    },
    [formik?.setFieldValue, countries]
  );

  return (
    <>
      <FormikAddressGoogleAutocomplete
        label={t(`${translationKey}.line1`)}
        id="addressLine1"
        name="addressLine1"
        autocompleteField="addressLine1"
        formName={formName}
        required
        assignAddressDetails={setFormikAddressValues}
      />
      {extraAddressLine}
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem',
          flexDirection: 'row',
          ...((isMobile || allFieldsFullWidth) && {
            gap: 0,
            flexDirection: 'column',
          }),
        }}
      >
        <FormikTextField
          label={t(`${translationKey}.city`)}
          id="town"
          name="town"
          formName={formName}
          required
        />
        <FormikTextField
          label={t(`${translationKey}.postcode`)}
          id="postCode"
          name="postCode"
          formName={formName}
          required
        />
      </Box>
      <Box
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'flex-start',
          gap: '1rem',
          flexDirection: 'row',
          ...((isMobile || allFieldsFullWidth) && {
            gap: 0,
            flexDirection: 'column',
          }),
        }}
      >
        <FormikSelect
          label={t(`${translationKey}.country`)}
          id="country"
          name="country"
          formName={formName}
          autoComplete="country"
          required
          options={countries}
        />
        {countyList?.length > 0 && (
          <FormikSelect
            label={t(`${translationKey}.state`)}
            id="county"
            name="county"
            formName={formName}
            options={countyList}
            required
          />
        )}
      </Box>
    </>
  );
};

export default AddressFormWithGoogleAutocomplete;
